"use client"

import { useEffect, useRef } from "react"
import { AlertCircle } from "lucide-react"

import { cn } from "@/lib/utils"

interface ScanQuotaBannerProps {
  remaining: number
  /** Extra scans a free account unlocks after the anonymous allowance is spent. */
  unlockCount?: number
  /** Fired once when the banner first renders. */
  onShown?: () => void
  /** Fired when the user clicks through to the save ask. */
  onSaveClick?: () => void
  className?: string
}

/**
 * Soft quota warning shown on an anon result after the no-account allowance is
 * spent. Asks on the successful result moment instead of waiting for a second
 * blocked upload.
 */
export function ScanQuotaBanner({
  remaining,
  unlockCount = 0,
  onShown,
  onSaveClick,
  className,
}: ScanQuotaBannerProps) {
  const shownRef = useRef(false)
  useEffect(() => {
    if (shownRef.current) return
    shownRef.current = true
    onShown?.()
  }, [onShown])

  const handleClick = () => {
    onSaveClick?.()
    document
      .getElementById("care-plan-upgrade")
      ?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const isUnlockPrompt = remaining <= 0 && unlockCount > 0
  const headline = isUnlockPrompt
    ? `Create a free account for ${unlockCount} more grass IDs this month`
    : remaining === 1
      ? "1 free ID left this month"
      : `${remaining} free IDs left this month`
  const body = isUnlockPrompt
    ? "Save this result and keep scanning."
    : "Save your lawn to keep your scans and plan."

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3",
        className,
      )}
    >
      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" strokeWidth={2.25} />
      <p className="flex-1 text-[13px] text-amber-900 leading-snug">
        <span className="font-semibold">
          {headline}
        </span>
        {". "}
        {body}
      </p>
      <button
        type="button"
        onClick={handleClick}
        className="flex-shrink-0 text-[13px] font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900 transition-colors"
      >
        Save it free
      </button>
    </div>
  )
}
